import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import {
  type CreateTriggerRequest,
  type TriggerRecord,
  type UpdateTriggerRequest,
} from "../TriggerRecord";

import { forgetTrigger, storeTrigger } from "./triggerCache";

// The mutations below don't wait for the list's refetch: they settle as soon
// as the server confirms, which the cache write already shows.

export function useCreateTrigger(orgId?: string) {
  const { triggerService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateTriggerRequest) =>
      triggerService.createTrigger(request, resolvedOrgId),
    onSuccess: (record: TriggerRecord) => {
      void storeTrigger(queryClient, record);
    },
  });
}

export function useUpdateTrigger() {
  const { triggerService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      triggerId,
      updates,
    }: {
      triggerId: string;
      updates: UpdateTriggerRequest;
    }) => triggerService.updateTrigger(triggerId, updates),
    onSuccess: (record: TriggerRecord) => {
      void storeTrigger(queryClient, record);
    },
  });
}

/**
 * Enables or disables `trigger` when `toggle` is called. While the request is
 * in flight, `checked` shows the requested state. Otherwise it shows
 * `trigger.enabled`.
 */
export function useToggleTrigger(
  trigger: TriggerRecord,
  onError: (error: Error) => void,
): { checked: boolean; pending: boolean; toggle: (enabled: boolean) => void } {
  const { triggerService } = useDomainServices();
  const queryClient = useQueryClient();

  const { mutate, isPending, variables } = useMutation({
    mutationFn: (enabled: boolean) =>
      triggerService.setEnabled(trigger.trigger_id, enabled),
    onSuccess: (record: TriggerRecord) => {
      void storeTrigger(queryClient, record);
    },
    onError,
  });
  return {
    checked: isPending ? variables : trigger.enabled,
    pending: isPending,
    toggle: mutate,
  };
}

/**
 * Deletes the trigger with this id.
 */
export function useDeleteTrigger() {
  const { triggerService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      trigger,
    }: {
      trigger: Pick<TriggerRecord, "trigger_id" | "org_id">;
    }) => triggerService.deleteTrigger(trigger.trigger_id),
    onSuccess: (_, { trigger }) => {
      void forgetTrigger(queryClient, trigger);
    },
  });
}
